import { useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'

import { IAppLayoutProps, ILayoutStyles } from './AppLayout.types'
import * as S from './AppLayout.styles'

import { AppHeader } from '@/components/common/AppHeader'
import { SideNav, type NavItemType } from '@/components/common/SideNav'
import { MobileNav } from '@/components/common/MobileNav'
import { DocumentHead } from '@/components/common/DocumentHead'
import { FeedbackModal } from '@/components/common/FeedbackModal'
import { selectUserViewType } from '@/store/selectors/authSelectors'
import { useUI } from '@/contexts/UIContext'
import { useResponsive } from '@/hooks/useResponsive'
import { UI_CONSTANTS } from '@/constants/app'
import { useAppNavigation } from '@/hooks/useAppNavigation'
import { GlobalOnboardingBanner } from '@/components/common/GlobalOnboardingBanner'
import { useFavicon } from '@/hooks/useFavicon'

export const AppLayout = ({ children }: IAppLayoutProps) => {
    const currentView = useSelector(selectUserViewType)
    const { navigateToPath } = useAppNavigation()
    const { sideNavExpanded, setSideNavExpanded, showFeedbackModal, setShowFeedbackModal } = useUI()
    const { isMobile } = useResponsive()
    const location = useLocation()

    // Get current navigation item from URL path
    const getCurrentNavItem = (): NavItemType | undefined => {
        const path = location.pathname.substring(1) // Remove leading slash

        // Simple path-based navigation item detection (no org prefix needed)
        if (path === 'dashboard') return 'dashboard'
        if (path.startsWith('team')) return 'team'
        if (path === 'surveys') return 'surveys'
        if (path === 'kudos') return 'kudos'
        if (path === 'notification-settings') return 'notification-settings'
        if (path === 'reports') return 'reports'
        if (path === 'upload' || path.startsWith('upload/')) return 'upload'
        if (path === 'alerts') return 'alerts'
        if (path === 'people') return 'people'
        if (path === 'learn') return 'learn'
        if (path === 'brag') return 'brag'
        if (path === 'career') return 'career'
        if (path === 'documentation') return 'documentation'

        return undefined
    }

    useFavicon('/assets/favicon.svg')

    const [mobileNavOpen, setMobileNavOpen] = useState(false)

    const handleSideNavNavigate = (item: NavItemType) => {
        // Use direct navigation for all items (unified upload structure)
        navigateToPath(`/${item}`)
    }

    const handleSideNavHover = (expanded: boolean) => {
        if (!isMobile) {
            setSideNavExpanded(expanded)
        }
    }

    const getLayoutStyles = (): ILayoutStyles => {
        if (isMobile) {
            return {
                contentMargin: '0px',
                showDesktopSidebar: false,
            }
        }

        // Add margin for collapsed sidebar width so content isn't hidden
        return {
            contentMargin: `${UI_CONSTANTS.SIDEBAR_WIDTH_COLLAPSED}px`,
            showDesktopSidebar: true,
        }
    }

    const { contentMargin, showDesktopSidebar } = getLayoutStyles()

    return (
        <S.Container>
            <DocumentHead />

            <AppHeader
                showMobileNav={isMobile}
                mobileNavTrigger={
                    isMobile && currentView ? (
                        <MobileNav
                            userType={currentView}
                            currentItem={getCurrentNavItem()}
                            onNavigate={handleSideNavNavigate}
                            isOpen={mobileNavOpen}
                            onOpenChange={setMobileNavOpen}
                        />
                    ) : null
                }
            />

            <S.MainWrapper>
                <S.ContentArea
                    $marginLeft={contentMargin}
                    $transitionDuration={UI_CONSTANTS.ANIMATION_DURATION}
                    $isMobile={isMobile}
                >
                    {/* Onboarding banner (hidden internally when not applicable) */}
                    <S.BannerWrapper $isMobile={isMobile}>
                        <GlobalOnboardingBanner />
                    </S.BannerWrapper>
                    <S.ChildrenWrapper $isMobile={isMobile}>{children}</S.ChildrenWrapper>
                </S.ContentArea>
            </S.MainWrapper>

            {/* Sidebar positioned independently, overlays content */}
            {showDesktopSidebar && currentView && (
                <SideNav
                    userType={currentView}
                    currentItem={getCurrentNavItem()}
                    onNavigate={handleSideNavNavigate}
                    isExpanded={sideNavExpanded}
                    onHover={handleSideNavHover}
                    isMobile={false}
                />
            )}

            {/* Don't show sidebar if user role is unmapped - just hide it completely */}

            {isMobile && mobileNavOpen && <S.MobileOverlay onClick={() => setMobileNavOpen(false)} />}

            {/* Feedback Modal */}
            <FeedbackModal
                isOpen={showFeedbackModal}
                onClose={() => setShowFeedbackModal(false)}
                currentScreen={location.pathname.replace('/', '').replace('-', ' ') || 'dashboard'}
            />
        </S.Container>
    )
}
